// @ts-nocheck
import jwtDecode from 'jwt-decode'
import UserTokens from "./UserTokens"
import UserInfo from "./UserInfo"
import CookieParser from "./CookieParser"
import { deserialize } from '../utils/isJSONFormat.util'
import useLoading from "../composables/useLoading"
import createUser from "../api/user/createUser"
import login from "../api/user/login"
import logout from "../api/user/logout"
import refresh from "../api/user/refresh"
import updateUser from "../api/user/updateUser"

const routes = {
    home: '/frontend/index/index.html',
    login: '/frontend/login/login.html',
    previousRoute: sessionStorage.getItem('previousRoute')
}

const TOKEN_COOKIE_NAMES = {
    idToken: 'tokens_idToken',
    accessToken: 'tokens_accessToken',
    refreshToken: 'tokens_refreshToken',
    legacy: 'tokens'
}

const COOKIE_LIMIT_BYTES = 4096

function setTokenCookies(tokens) {
    document.cookie = `${TOKEN_COOKIE_NAMES.idToken}=${tokens.idToken};path=/`
    document.cookie = `${TOKEN_COOKIE_NAMES.accessToken}=${tokens.accessToken};path=/`
    if (tokens.refreshToken) {
        document.cookie = `${TOKEN_COOKIE_NAMES.refreshToken}=${tokens.refreshToken};path=/`
    }
    const legacyStr = JSON.stringify(tokens)
    const legacySize = new Blob([legacyStr]).size
    if (legacySize <= COOKIE_LIMIT_BYTES) {
        document.cookie = `${TOKEN_COOKIE_NAMES.legacy}=${legacyStr};path=/`
        console.log('[auth] setTokenCookies: stored 3 cookies + legacy (under 4KB)')
    } else {
        console.log('[auth] setTokenCookies: stored 3 cookies only (payload over 4KB, new flow)')
    }
}

function clearTokenCookies() {
    const expires = new Date(0).toUTCString()
    const clear = (name) => { document.cookie = `${name}=;path=/;expires=${expires}` }
    clear(TOKEN_COOKIE_NAMES.idToken)
    clear(TOKEN_COOKIE_NAMES.accessToken)
    clear(TOKEN_COOKIE_NAMES.refreshToken)
    clear(TOKEN_COOKIE_NAMES.legacy)
    console.log('[auth] clearTokenCookies: cleared all token cookies')
}

class User {
    protected tokens: UserTokens
    protected info: UserInfo

    constructor() {
        this.tokens = new UserTokens()
        this.info = new UserInfo()
        this.setLastLogin(Date.now())
    }

    async init(): Promise<void> {
        if (location.pathname !== routes.login) {
            // the second someone tries accessing a page, it becomes previousRoute
            routes.previousRoute = location.pathname
            sessionStorage.setItem('previousRoute', routes.previousRoute)
        }

        // retrieve data from persistent storage
        this.setUserInfo(User.extractUserInfoFromLocalStorage())
        this.setTokens(User.extractUserTokensFromCookies())

        // if we have expired user info, try to refresh
        const { startLoadingThis, stopLoadingThis } = useLoading()
        if (this.getIsHaveUserInfo() && User.getIsTokenExpired(this.getIdToken())) {
            startLoadingThis()
            const res = await this.refresh()
            stopLoadingThis()
            return res
        }

        this.checkLogin()
    }

    checkLogin() {
        const { home, login, previousRoute } = routes
        const currentPath = location.pathname.replace(/\/$/, '') || '/'

        // preferred routes based on role
        let defaultHome = home
        if (this.getIsUserLoggedIn()) {
            if (this.getRoles().includes('B2B')) {
                defaultHome = './index.html'
            }
        }

        let newPath = this.getIsUserLoggedIn() ? (previousRoute ? previousRoute : defaultHome) : login
        newPath = newPath.replace(/\/$/, '') || newPath

        if (newPath === currentPath) {
            sessionStorage.removeItem('auth_redirect_count')
            sessionStorage.removeItem('auth_redirect_time')
            return
        }

        if (process.env.NODE_ENV !== 'production') {
            console.log('redirection blocked for localhost because of path issue')
            return
        }

        const now = Date.now()
        const redirectCount = parseInt(sessionStorage.getItem('auth_redirect_count') || '0', 10)
        const redirectTime = parseInt(sessionStorage.getItem('auth_redirect_time') || '0', 10)
        if (redirectCount >= 3 && now - redirectTime < 10000) {
            console.error('[auth] Redirect loop detected (3+ redirects in 10s), staying on current page')
            sessionStorage.removeItem('auth_redirect_count')
            sessionStorage.removeItem('auth_redirect_time')
            return
        }
        sessionStorage.setItem('auth_redirect_count', String(redirectCount + 1))
        sessionStorage.setItem('auth_redirect_time', String(now))

        if (currentPath === login && newPath !== login && redirectCount >= 1) {
            newPath = defaultHome
        }

        location.pathname = newPath
    }

    /**
     * SETTERS
     */
    public setIdToken(newVal: string) {
        this.tokens.idToken = newVal
    }
    public setAccessToken(newVal: string) {
        this.tokens.accessToken = newVal
    }
    public setUsername(newVal: string) {
        this.info.username = newVal
    }
    public setEmail(newVal: string) {
        this.info.email = newVal
    }
    public setRoles(newVal: string[]) {
        if (!Array.isArray(newVal)) throw new Error("newVal must be an array")
        this.info.roles = newVal
    }
    public setLastLogin(newVal: number) {
        this.info.lastLogin = newVal
    }
    public setUserInfo(newVal: any | null) {
        if (!newVal) this.info = new UserInfo()
        else this.info = User.buildUserInfo(newVal)
    }
    public setTokens(newVal: any | null) {
        if (!newVal) this.tokens = new UserTokens()
        else this.tokens = User.buildTokens(newVal)
    }


    /**
     * GETTERS
     */

    public getIdToken() {
        return this.tokens.idToken
    }
    public getAccessToken() {
        return this.tokens.accessToken
    }
    public getUsername() {
        return this.info.username
    }
    public getEmail() {
        return this.info.email
    }
    public getRoles() {
        return this.info.roles
    }
    public getLastLogin() {
        return this.info.lastLogin
    }
    public getTokens() {
        return this.tokens
    }
    public getInfo() {
        return this.info
    }


    /** 
     * STATIC METHODS 
     */
    static buildUserInfo(obj: any): UserInfo {
        if (typeof obj.username !== "string") throw new Error("username must be a string")
        if (typeof obj.email !== "string") throw new Error("email must be a string")
        if (!Array.isArray(obj.roles)) throw new Error("roles must be an array")
        if (typeof obj.lastLogin !== "number") throw new Error("lastLogin must be a number")

        const userInfo = new UserInfo()
        userInfo.username = obj.username
        userInfo.email = obj.email
        userInfo.roles = obj.roles
        userInfo.lastLogin = obj.lastLogin
        return userInfo
    }
    static buildTokens(obj: any): UserTokens {
        if (typeof obj.idToken !== "string") throw new Error("idToken must be a string")
        if (typeof obj.accessToken !== "string") throw new Error("accessToken must be a string")

        const tokens = new UserTokens()
        tokens.idToken = obj.idToken
        tokens.accessToken = obj.accessToken
        return tokens
    }

    static getIsTokenExpired(token: string, timeLimit = 60 * 60 * 1000 /* default 1 hour */): boolean {
        if (!token) return true
        const { exp } = jwtDecode(token)
        return !(Date.now() - exp * 1000 < timeLimit)
    }

    static extractUserInfoFromLocalStorage(): UserInfo {
        const userInfoFromLocalStorage: { [key: string]: any } = deserialize(localStorage.getItem('userInfo') || "{}")

        // if nothing found in LocalStorage - return empty object
        if (Object.keys(userInfoFromLocalStorage).length === 0) return new UserInfo()

        // try to validate and build the object
        try {
            const userInfo: UserInfo = User.buildUserInfo(userInfoFromLocalStorage)
            return userInfo
        }
        // in case of invalid data - remove object from LocalStorage and return an empty object
        catch (err) {
            console.error(err)
            localStorage.removeItem('userInfo')
            return new UserInfo()
        }
    }

    static extractUserTokensFromCookies(): UserTokens {
        const c = CookieParser.cookies
        const idToken = c[TOKEN_COOKIE_NAMES.idToken]
        const accessToken = c[TOKEN_COOKIE_NAMES.accessToken]

        if (idToken && accessToken) {
            try {
                const tokens = User.buildTokens({ idToken, accessToken })
                console.log('[auth] extractUserTokensFromCookies: read from 3 cookies')
                return tokens
            } catch (err) {
                console.error('[auth] extractUserTokensFromCookies: buildTokens failed (3 cookies)', err)
                clearTokenCookies()
                return new UserTokens()
            }
        }

        const legacy = deserialize(c[TOKEN_COOKIE_NAMES.legacy])
        if (Object.keys(legacy).length === 0) {
            console.log('[auth] extractUserTokensFromCookies: no token cookies')
            return new UserTokens()
        }

        try {
            const tokens = User.buildTokens(legacy)
            console.log('[auth] extractUserTokensFromCookies: read from legacy tokens cookie')
            return tokens
        } catch (err) {
            console.error('[auth] extractUserTokensFromCookies: buildTokens failed (legacy)', err)
            clearTokenCookies()
            return new UserTokens()
        }
    }

    /** API */
    public async createUser(data: { username: string, email: string, password: string, confirmPassword: string }): Promise<void> {
        const { username, email, password, confirmPassword } = data
        const { startLoadingThis, stopLoadingThis } = useLoading()

        if (password !== confirmPassword) {
            throw new Error("Passwords don't match")
        }

        try {
            startLoadingThis()
            const { data } = await createUser(username, email, password)
            // console.log(data)
        } catch (err) {
            throw err
        } finally {
            stopLoadingThis()
        }
    }

    public async updateUser(data: { username: string, attributes: any }): Promise<void> {
        const { username, attributes } = data
        const { startLoadingThis, stopLoadingThis } = useLoading()
        try {
            startLoadingThis()
            const { data } = await updateUser(username, attributes)
            // console.log(data)
        } catch (err) {
            throw err
        } finally {
            stopLoadingThis()
        }
    }

    public async login(data: { username: string, password: string }): Promise<void> {
        const { username, password } = data
        const { startLoadingThis, stopLoadingThis } = useLoading()
        try {
            startLoadingThis()

            const {
                data: { userInfo, tokens },
                error
            } = await login(username, password)

            if (error) throw new Error(error)

            this.setUserInfo(userInfo)
            this.setTokens(tokens)
            localStorage.setItem('userInfo', JSON.stringify(userInfo))
            setTokenCookies(tokens)
            console.log('[auth] login: success, tokens stored')

        } catch (err: any) {
            console.log(err)
            throw new Error(err.message)
        } finally {
            this.checkLogin()
            stopLoadingThis()
        }
    }


    public async refresh(): Promise<void> {
        const { startLoadingThis, stopLoadingThis } = useLoading()

        try {
            startLoadingThis()
            const {
                data: { userInfo, tokens }
            } = await refresh()

            this.setUserInfo(userInfo)
            this.setTokens(tokens)
            setTokenCookies(tokens)
            console.log('[auth] refresh: success, tokens stored')
        } catch (err: any) {
            // if we get error, it means unsuccessful refresh, so we need to save page
            console.log(err)
            // also clear persistent data
            this.setUserInfo(null)
            this.setTokens(null)
        } finally {
            this.checkLogin()
            stopLoadingThis()
        }
    }


    public async logout(): Promise<void> {
        const { startLoadingThis, stopLoadingThis } = useLoading()
        const accessToken: string = this.getAccessToken()

        try {
            startLoadingThis()
            await logout(accessToken)
        } catch (err) {
            console.log('logout', err)
        } finally {
            clearTokenCookies()
            this.setUserInfo(null)
            this.setTokens(null)
            console.log('[auth] logout: cookies cleared, redirecting')
            this.checkLogin()
            stopLoadingThis()
        }
    }


    /**
     * PUBLIC METHODS
     */

    public getIsHaveUserInfo(): boolean {
        return !!(this.getIdToken() && this.getAccessToken() && this.getUsername() && this.getEmail())
    }

    public getIsUserLoggedIn(): boolean {
        this.setTokens(User.extractUserTokensFromCookies())
        return !!(this.getIsHaveUserInfo() && !User.getIsTokenExpired(this.getIdToken()))
    }

    async authRequest(service: any, args: any) {
        const { startLoadingThis, stopLoadingThis } = useLoading()
        startLoadingThis()
        if (User.getIsTokenExpired(this.getIdToken())) {
            await this.refresh()
        }
        const data = await service(args, { token: this.getIdToken() })
        stopLoadingThis()
        return data
    }


}

export default User